import { format } from 'date-fns';
import humanizeDuration from 'humanize-duration';

/**
 * Formats a number into a short string representation with K/M suffixes
 * @param value - The number to format
 * @returns A string representation of the number with appropriate suffix
 * @example
 * formatShortNumber(1500) // "1.5K"
 * formatShortNumber(1500000) // "1.5M"
 * formatShortNumber(500) // "500"
 */
export const formatShortNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

/**
 * Formats seconds into a concise human-readable duration string (limited to hours max)
 * @param seconds - The number of seconds to format
 * @returns A concise duration string, or empty string if no data
 */
export const formatDurationFromSeconds = (
  seconds: number | undefined | null,
  options?: humanizeDuration.Options,
): string => {
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) return '';

  if (seconds === 0) return '0 sec';

  const defaultOptions: humanizeDuration.Options = {
    largest: 2, // Show at most 2 units (e.g., "1 hr, 30 min")
    round: true, // Round to avoid decimals
    units: ['h', 'm', 's'], // Only show hours, minutes, and seconds (no days, weeks, months, years)
    spacer: '',
    delimiter: ' ',
  };

  return shortHumanizer(seconds * 1000, { ...defaultOptions, ...options });
};

/**
 * Returns a date formatting strategy based on the provided date resolution
 * @param dateResolution - The resolution type (DAILY, WEEKLY, MONTHLY, YEARLY)
 * @returns A function that formats dates according to the resolution
 */
export const getFormatStrategy = (dateResolution: string | null) => {
  const formatStrategies = {
    DAILY: (date: Date) => format(date, 'M/d/yyyy'),
    WEEKLY: (date: Date) => format(date, 'M/d/yyyy'),
    MONTHLY: (date: Date) => format(date, 'MMMM yyyy'),
    YEARLY: (date: Date) => format(date, 'yyyy'),
  } as const;

  type ResolutionType = keyof typeof formatStrategies;
  return !dateResolution || !(dateResolution in formatStrategies)
    ? formatStrategies.MONTHLY
    : formatStrategies[dateResolution as ResolutionType];
};

// Create a custom humanizer with abbreviated English
const shortHumanizer = humanizeDuration.humanizer({
  language: 'shortEn',
  languages: {
    shortEn: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
      ms: () => 'ms',
    },
  },
});
